import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from 'recharts';

const CompareBarChart = ({ data, year1, year2, title }) => {
  // Transform data for the chart
  const chartData = data.map(item => ({
    name: item.metric_name.length > 15 
      ? item.metric_name.substring(0, 15) + '...' 
      : item.metric_name,
    fullName: item.metric_name,
    [year1]: item[`year_${year1}`]?.actual || 0,
    [year2]: item[`year_${year2}`]?.actual || 0,
    unit: item.unit,
    change_pct: item.change_pct,
    directionality: item.directionality
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const unit = data?.unit || '';
      
      const formatValue = (value) => {
        if (unit === '$M' || unit === '$') {
          return `$${value.toLocaleString()}`;
        }
        if (unit === '%') {
          return `${value.toFixed(1)}%`;
        }
        return value.toLocaleString();
      };

      return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg p-3">
          <p className="font-medium text-[#111827] mb-2">{data?.fullName || label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-[#6B7280]">{entry.name}:</span>
              <span className="font-medium text-[#111827]">{formatValue(entry.value)}</span>
            </div>
          ))}
          {data?.change_pct !== null && data?.change_pct !== undefined && (
            <div className="mt-2 pt-2 border-t border-[#E5E7EB]">
              <span className={`text-sm font-medium ${
                (data.directionality === 'lower_is_better' ? data.change_pct < 0 : data.change_pct > 0)
                  ? 'text-[#059669]'
                  : data.change_pct === 0 
                    ? 'text-[#6B7280]'
                    : 'text-[#DC2626]'
              }`}>
                Change: {data.change_pct > 0 ? '+' : ''}{(data.change_pct * 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[#F9FAFB] rounded-lg p-4" data-testid="compare-bar-chart">
      <h3 className="text-sm font-semibold text-[#374151] mb-4">{title}</h3>
      
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            barGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 11, fill: '#6B7280' }}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickFormatter={(value) => {
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                return value;
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              formatter={(value) => <span className="text-sm text-[#374151]">{value}</span>}
            />
            <Bar 
              dataKey={year1} 
              fill="#94A3B8" 
              radius={[4, 4, 0, 0]}
              name={String(year1)}
            />
            <Bar 
              dataKey={year2} 
              fill="#2563EB" 
              radius={[4, 4, 0, 0]}
              name={String(year2)}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Change Summary */}
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {chartData.slice(0, 6).map((item, index) => {
          const isGood = item.directionality === 'lower_is_better' 
            ? item.change_pct < 0 
            : item.change_pct > 0;
          const isBad = item.directionality === 'lower_is_better' 
            ? item.change_pct > 0 
            : item.change_pct < 0;
          
          return (
            <div 
              key={index}
              className={`text-xs px-2 py-1 rounded-full ${
                item.change_pct === null || item.change_pct === undefined
                  ? 'bg-[#F3F4F6] text-[#6B7280]'
                  : isGood
                    ? 'bg-[#D1FAE5] text-[#065F46]'
                    : isBad
                      ? 'bg-[#FEE2E2] text-[#991B1B]'
                      : 'bg-[#F3F4F6] text-[#6B7280]'
              }`}
            >
              {item.name}: {item.change_pct !== null && item.change_pct !== undefined 
                ? `${item.change_pct > 0 ? '+' : ''}${(item.change_pct * 100).toFixed(1)}%`
                : 'N/A'}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompareBarChart;
